// The one gaze-limit model: a yaw/pitch box for authoring, a cone for look-at safety.

import { quaternion, scalar, vector3 } from '../../math/index.ts'
import type { Vector3 } from '../../math/index.ts'

export type GazeLimits = {
  yawDegrees: number
  pitchDegrees: number
  coneDegrees: number
}

// Anatomically loose, enough to stop a bad target rotating an eyeball into the skull.
export const DEFAULT_GAZE_LIMITS: GazeLimits = { yawDegrees: 35, pitchDegrees: 25, coneDegrees: 35 }

export type YawPitch = { yawDegrees: number, pitchDegrees: number }

export function clampYawPitch(request: YawPitch, limits: GazeLimits): YawPitch {
  return {
    yawDegrees: scalar.clampFinite(request.yawDegrees, -limits.yawDegrees, limits.yawDegrees),
    pitchDegrees: scalar.clampFinite(request.pitchDegrees, -limits.pitchDegrees, limits.pitchDegrees),
  }
}

// Yaw about world up, pitch about the eye's own right axis, applied to its rest axis.
export function directionFromYawPitch(restAxis: Vector3, request: YawPitch): Vector3 {
  const axis = vector3.normalize(restAxis)
  let right = vector3.cross(vector3.UP, axis)
  if (vector3.length(right) < 1e-6) right = [1, 0, 0]
  const pitch = quaternion.fromAxisAngle(right, scalar.degreesToRadians(-request.pitchDegrees))
  const yaw = quaternion.fromAxisAngle(vector3.UP, scalar.degreesToRadians(request.yawDegrees))
  return vector3.normalize(quaternion.rotate(quaternion.multiply(yaw, pitch), axis))
}

// Hold a look direction within the cone, as close to the request as the limit allows.
export function limitToCone(restAxis: Vector3, target: Vector3, coneDegrees: number): Vector3 {
  const axis = vector3.normalize(restAxis)
  const direction = vector3.normalize(target)
  const limit = scalar.degreesToRadians(coneDegrees)
  if (vector3.angleBetweenUnits(axis, direction) <= limit) return direction
  const turnAxis = vector3.cross(axis, direction)
  // Antiparallel: no unique way to turn, so hold the rest axis rather than pick one.
  if (vector3.length(turnAxis) < 1e-9) return axis
  return vector3.normalize(quaternion.rotate(quaternion.fromAxisAngle(turnAxis, limit), axis))
}
